import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Snackbar,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  Save,
  CalendarToday
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { teacherAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const ExamSchedule = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // class ID
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classData, setClassData] = useState(null);
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState('');
  const [schedule, setSchedule] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchClassAndExams = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch class info
      const classResponse = await teacherAPI.getClasses();
      const classes = classResponse.data.classes || [];
      const currentClass = classes.find(c => c.id === id);
      setClassData(currentClass);

      // Fetch active exams
      const examsResponse = await teacherAPI.getClassExams(id);
      setExams(examsResponse.data.exams || []);
      
      // Auto-select first exam if available
      if (examsResponse.data.exams && examsResponse.data.exams.length > 0) {
        setSelectedExam(examsResponse.data.exams[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Error fetching exam data', 'error');
    } finally {
      setLoading(false);
    }
  } , [id]);

  const fetchExamSchedule = useCallback(async () => {
    try {
      const response = await teacherAPI.getExamSchedule(id, selectedExam);
      setSchedule(response.data.schedule || []);
    } catch (error) {
      console.error('Error fetching exam schedule:', error);
      showSnackbar('Error fetching exam schedule', 'error');
    }
  }, [id, selectedExam]);

  useEffect(() => {
    fetchClassAndExams();
  }, [fetchClassAndExams]);

  useEffect(() => {
    if (selectedExam) {
      fetchExamSchedule();
    }
  }, [selectedExam, fetchExamSchedule]);

  const handleScheduleChange = (subjectId, field, value) => {
    setSchedule(prevSchedule => 
      prevSchedule.map(item => 
        item.subject_id === subjectId
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Filter only subjects that have an exam date
      const schedulesToSave = schedule
        .filter(item => item.exam_date)
        .map(item => ({
          subject_id: item.subject_id,
          exam_date: item.exam_date,
          start_time: item.start_time || null,
          end_time: item.end_time || null,
          max_marks: item.max_marks || 100,
          notes: item.notes || null
        }));

      if (schedulesToSave.length === 0) {
        showSnackbar('Please set at least one exam date', 'warning');
        return;
      }

      await teacherAPI.setExamSchedule(id, selectedExam, { schedules: schedulesToSave });
      showSnackbar('Exam schedule saved successfully', 'success');
      fetchExamSchedule();
    } catch (error) {
      console.error('Error saving exam schedule:', error);
      showSnackbar('Error saving exam schedule', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!classData) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
        <Typography variant="h6" color="error">
          Class not found
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Schedule Exam Dates
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {classData.name} - {classData.grade && `Grade ${classData.grade}`} {classData.section && `Section ${classData.section}`}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/teacher/classes/${id}/manage`)}
        >
          Back to Class
        </Button>
      </Box>

      {exams.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Active Exams Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The principal needs to create exams before you can schedule exam dates.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Exam Selector */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Select Exam</InputLabel>
              <Select
                value={selectedExam}
                label="Select Exam"
                onChange={(e) => setSelectedExam(e.target.value)}
              >
                {exams.map((exam) => (
                  <MenuItem key={exam.id} value={exam.id}>
                    {exam.name} - {exam.academic_year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          {/* Subject Schedule */}
          {selectedExam && schedule.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Schedule Exam Dates for Subjects
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Grid container spacing={3}>
                {schedule.map((item) => (
                  <Grid item xs={12} key={item.subject_id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">
                          {item.subject_name}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              type="date"
                              label="Exam Date"
                              value={item.exam_date || ''}
                              onChange={(e) => handleScheduleChange(item.subject_id, 'exam_date', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              required
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <TextField
                              fullWidth
                              type="time"
                              label="Start Time"
                              value={item.start_time || ''}
                              onChange={(e) => handleScheduleChange(item.subject_id, 'start_time', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <TextField
                              fullWidth
                              type="time"
                              label="End Time"
                              value={item.end_time || ''}
                              onChange={(e) => handleScheduleChange(item.subject_id, 'end_time', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Max Marks"
                              value={item.max_marks || 100}
                              onChange={(e) => handleScheduleChange(item.subject_id, 'max_marks', parseInt(e.target.value))}
                              inputProps={{ min: 0 }}
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            {item.exam_date && (
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  height: '100%',
                                  color: 'success.main'
                                }}
                              >
                                <CalendarToday sx={{ mr: 1 }} />
                                <Typography variant="body2">Scheduled</Typography>
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Box mt={4} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                  onClick={handleSave}
                  disabled={saving}
                  sx={{ px: 6 }}
                >
                  {saving ? 'Saving...' : 'Save Exam Schedule'}
                </Button>
              </Box>
            </Paper>
          )}

          {selectedExam && schedule.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Subjects Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please add subjects to this class before scheduling exams.
              </Typography>
            </Paper>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ExamSchedule;
